import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';

const KPI = ({ label, value, sub, color, icon }) => (
  <div className="card card-p" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div style={{
        width: '36px', height: '36px', borderRadius: '10px',
        background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <span style={{ color, fontSize: '16px' }}>{icon}</span>
      </div>
      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--green)', background: 'var(--green-dim)', padding: '2px 7px', borderRadius: '20px' }}>{sub}</span>
    </div>
    <div>
      <div style={{ fontSize: '30px', fontWeight: 700, letterSpacing: '-0.04em', fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>
        {typeof value === 'number' ? value.toLocaleString('el-GR') : (value ?? '—')}
      </div>
      <div style={{ color: 'var(--slate)', fontSize: '12px', marginTop: '5px', fontWeight: 500 }}>{label}</div>
    </div>
  </div>
);

const PALETTE = ['#0A0F1E','#1B4FD8','#C9930A','#059669','#7C3AED','#DC2626'];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/settings/stats')
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Καλημέρα' : hour < 18 ? 'Καλησπέρα' : 'Καλό βράδυ';

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', gap: '12px' }}>
      <span className="spin" style={{ width: '20px', height: '20px' }} />
      <span style={{ color: 'var(--slate)' }}>Φόρτωση...</span>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '28px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-0.03em' }}>
            {greeting}, {user.full_name?.split(' ')[0] || user.username}
          </h1>
          <p style={{ color: 'var(--slate)', fontSize: '13px', marginTop: '4px' }}>
            {new Date().toLocaleDateString('el-GR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/contacts')}>Επαφές →</button>
          {['admin','editor'].includes(user.role) && (
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/emails/compose')}>
              Νέα Αποστολή
            </button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '24px' }}>
        <KPI label="Σύνολο Επαφών"  value={stats?.total}       sub="Βάση" color="#1B4FD8" icon="👥" />
        <KPI label="Ενεργές Επαφές" value={stats?.active}      sub="Ενεργές" color="#059669" icon="✅" />
        <KPI label="Νέες τον μήνα"  value={stats?.this_month}  sub="Αυτόν τον μήνα" color="#7C3AED" icon="📈" />
        <KPI label="Emails Εστάλησαν" value={stats?.emails_sent} sub="Συνολικά" color="#C9930A" icon="📧" />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* Top Cities */}
        <div className="card card-p">
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '18px', letterSpacing: '-0.02em' }}>
            Κορυφαίες Πόλεις
          </h3>
          {stats?.top_cities?.length ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={stats.top_cities} layout="vertical" barSize={12}>
                <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--mist)' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="city" tick={{ fontSize: 11, fill: 'var(--slate)' }} width={72} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid var(--silver)', fontSize: '12px', boxShadow: 'var(--shadow-md)' }} formatter={v => [v, 'Επαφές']} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {stats.top_cities.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>

        {/* Top Industries */}
        <div className="card card-p">
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '18px', letterSpacing: '-0.02em' }}>
            Κορυφαίοι Κλάδοι
          </h3>
          {stats?.top_industries?.length ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={stats.top_industries} layout="vertical" barSize={12}>
                <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--mist)' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="industry" tick={{ fontSize: 11, fill: 'var(--slate)' }} width={90} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid var(--silver)', fontSize: '12px', boxShadow: 'var(--shadow-md)' }} formatter={v => [v, 'Επαφές']} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {stats.top_industries.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </div>
      </div>

      <style>{`@media (max-width: 768px) { .charts-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

const EmptyChart = () => (
  <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--cloud)', fontSize: '13px' }}>
    Δεν υπάρχουν δεδομένα ακόμα
  </div>
);
